import { useEffect, useRef } from "react";
import { getAuth } from "firebase/auth";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

export default function useIdleStatus(timeoutMs = 3 * 60 * 1000) {
  const idleTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const auth = getAuth();

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);

    const goIdle = () => {
      updateDoc(userRef, {
        status: "idle",
        lastActive: serverTimestamp(),
      });
    };

    const goActive = () => {
      updateDoc(userRef, {
        status: "online",
      });
      resetIdleTimer();
    };

    const resetIdleTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(goIdle, timeoutMs);
    };

    const handleActivity = () => {
      if (document.visibilityState === "visible") {
        goActive();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        goActive();
      }
      // If they switch tab → let idle timer handle it
    };

    // ✅ Start a heartbeat: update lastActive every 60 seconds while online
    heartbeatTimer.current = setInterval(() => {
      updateDoc(userRef, {
        lastActive: serverTimestamp(),
      });
    }, 60 * 1000); // every 60 seconds

    // ✅ User interaction listeners
    const events = ["mousemove", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, handleActivity));
    document.addEventListener("visibilitychange", handleVisibilityChange);

    resetIdleTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
    };
  }, [timeoutMs]);
}
